/**
 * Module-level sync-signal store.
 *
 * Central, framework-agnostic place where the app records everything the sync
 * banner (and the service-worker update gate) needs to know:
 *   - which Firestore collections currently have un-acked local writes
 *   - how many photo uploads are pending in memory (non-durable — see spec §4)
 *   - the most recent hard write error, if any
 *
 * Why a module and not React state: the metadata flips on EVERY write, for every
 * collection. Routing that through component state would re-render every consumer
 * of every collection on each flip (the App.tsx double-render gotcha would make
 * it worse). Instead, listeners subscribe here and only the banner re-renders.
 * It is also readable from non-React callers — the SW update hook and the
 * service layer — which is why `reportWriteError` and `hasBlockingPendingWork`
 * live here rather than in a hook.
 *
 * See review/04-offline-write-spec.md §3.1 and §4.6.
 */

export interface WriteError {
  code: string;
  message: string;
  at: number; // epoch ms
}

export interface SyncSignals {
  /** True when ANY watched collection reports hasPendingWrites. */
  firestorePending: boolean;
  /** In-memory photo uploads awaiting retry. Non-durable; blocks app close. */
  pendingPhotoCount: number;
  /**
   * Work-order photos saved on the phone (IndexedDB) but not sent yet, across every job.
   * Durable, so it never blocks a reload; it only keeps the banner honest after the tech
   * leaves the job.
   */
  heldPhotoCount: number;
  /** Most recent non-retryable write rejection, cleared on success. */
  lastError: WriteError | null;
}

const pendingByCollection = new Map<string, boolean>();
let pendingPhotoCount = 0;
let heldPhotoCount = 0;
let blockingJobCount = 0;
let lastError: WriteError | null = null;

const listeners = new Set<() => void>();

const computeFirestorePending = (): boolean => {
  for (const pending of pendingByCollection.values()) {
    if (pending) return true;
  }
  return false;
};

// Cached snapshot with stable identity between changes — required by
// useSyncExternalStore, which loops forever if getSnapshot returns a fresh
// object each call.
let snapshot: SyncSignals = {
  firestorePending: false,
  pendingPhotoCount: 0,
  heldPhotoCount: 0,
  lastError: null,
};

const recompute = (): void => {
  snapshot = {
    firestorePending: computeFirestorePending(),
    pendingPhotoCount,
    heldPhotoCount,
    lastError,
  };
  for (const listener of listeners) listener();
};

/** Subscribe to any change in the signals. Returns an unsubscribe function. */
export const subscribeSyncSignals = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Stable snapshot for useSyncExternalStore. */
export const getSyncSignals = (): SyncSignals => snapshot;

/**
 * Record whether a collection's latest snapshot has un-acked local writes.
 * No-ops when the value is unchanged, so a stream of identical metadata (the
 * common case) never wakes the banner.
 */
export const setCollectionPending = (collectionName: string, pending: boolean): void => {
  if (pendingByCollection.get(collectionName) === pending) return;
  pendingByCollection.set(collectionName, pending);
  recompute();
};

/**
 * Marks the start of long-running work that lives only in memory — an AI run
 * whose results land one at a time, not a queued Firestore write. A reload
 * halfway through loses whatever has not been written yet, so this blocks the
 * service-worker update the same way a pending photo upload does. Returns an
 * idempotent release; always call it from a finally.
 */
export const beginBlockingJob = (): (() => void) => {
  blockingJobCount++;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    blockingJobCount = Math.max(0, blockingJobCount - 1);
  };
};

/** Report the count of in-memory pending photo uploads (JobDetail owns this). */
export const setPendingPhotoCount = (count: number): void => {
  const next = Math.max(0, count | 0);
  if (pendingPhotoCount === next) return;
  pendingPhotoCount = next;
  recompute();
};

/** Report how many work-order photos are saved on the phone but not sent (pendingPhotoStore owns this). */
export const setHeldPhotoCount = (count: number): void => {
  const next = Math.max(0, count | 0);
  if (heldPhotoCount === next) return;
  heldPhotoCount = next;
  recompute();
};

/** Record a non-retryable write failure so the banner can turn red immediately. */
export const reportWriteError = (code: string, message: string): void => {
  lastError = { code, message: message || 'A change could not be saved.', at: Date.now() };
  recompute();
};

/** Clear the error state after a successful write or a successful retry. */
export const reportWriteSuccess = (): void => {
  if (!lastError) return;
  lastError = null;
  recompute();
};

/**
 * True when there is non-durable work that a reload or tab-close would destroy:
 * pending photo uploads, and long-running in-memory jobs registered through
 * beginBlockingJob (the bid-walk AI runs, which take minutes and hold results
 * that are not written yet). Firestore's queued writes live in IndexedDB and
 * survive a reload, so they deliberately do NOT block — blurring this into
 * "block on any pending" would defeat the silent-update design that fixed the
 * v5.99.1 stale-build bug, for no gain. See spec §5.2.
 */
export const hasBlockingPendingWork = (): boolean => pendingPhotoCount > 0 || blockingJobCount > 0;

export const getPendingPhotoCount = (): number => pendingPhotoCount;
